/**
 * Centralized Analytics Event Names
 * Use these constants for all tracking events to ensure consistency
 */

// User Authentication Events
export const AUTH_EVENTS = {
  USER_SIGNED_UP: "User Signed Up", // When user completes registration
  USER_SIGNED_IN: "User Signed In", // When user successfully logs in
  USER_DAILY_LOGIN: "User Daily Login", // When user logs in for the first time in a day
  VERIFICATION_CODE_SENT: "Verification Code Sent", // When verification code is sent to user
} as const;

// Onboarding Events
export const ONBOARDING_EVENTS = {
  ONBOARDING_STEP_COMPLETED: "Onboarding Step Completed", // When user completes any onboarding step
  ONBOARDING_PROFILE_DETAILS_COMPLETED: "Onboarding Profile Details Completed", // When user completes profile setup
  USER_TYPE_SELECTED: "User Type Selected", // When user selects creator or founder type
  CREATOR_PROFILE_CREATED: "Creator Profile Created", // When creator profile is created
  ONBOARDING_COMPLETED: "Onboarding Completed", // When user completes entire onboarding flow
} as const;

// Social Account Events
export const SOCIAL_EVENTS = {
  CONNECTION_ATTEMPTED: "Social Account Connection Attempted", // When user tries to connect social account
  CONNECTION_SUCCESSFUL: "Social Account Connected Successfully", // When social account connection succeeds
  ADDITIONAL_ACCOUNT_CONNECTED: "Additional Social Account Connected", // When user connects additional social account
} as const;

// Stripe Payment Events
export const STRIPE_EVENTS = {
  // Payment Setup (Founder Cards)
  PAYMENT_METHOD_SETUP_INITIATED: "Stripe Payment Method Setup Initiated", // When user starts setting up payment method
  PAYMENT_METHOD_SETUP_COMPLETED: "Stripe Payment Method Setup Completed", // When payment method setup succeeds
  PAYMENT_METHOD_SETUP_FAILED: "Stripe Payment Method Setup Failed", // When payment method setup fails

  // Connect Account (Creator Payouts)
  CONNECT_ACCOUNT_CREATION_INITIATED:
    "Stripe Connect Account Creation Initiated", // When creator starts creating Stripe Connect account
  CONNECT_ACCOUNT_CREATION_FAILED: "Stripe Connect Account Creation Failed", // When Stripe Connect account creation fails
  CONNECT_ACCOUNT_ONBOARDING_STARTED:
    "Stripe Connect Account Onboarding Started", // When creator starts Stripe Connect onboarding
} as const;

// App/Campaign Management Events
export const APP_EVENTS = {
  APP_CREATED: "App Created", // When app is created
} as const;

// Partnership/Contract Events
export const PARTNERSHIP_EVENTS = {
  CAMPAIGN_JOINED: "Campaign Joined", // When creator joins a campaign
  APPLICATION_SUBMITTED: "Partnership Application Submitted", // When creator submits partnership application
} as const;

// Video Events
export const VIDEO_EVENTS = {
  VIDEO_SUBMITTED: "Video Submitted for Review", // When video is submitted for review
  VIDEO_AI_REVIEWED: "Video AI Reviewed", // When AI reviews video content
  VIDEO_APPROVED: "Video Approved", // When video is approved by reviewer
  VIDEO_REJECTED: "Video Rejected", // When video is rejected by reviewer
} as const;

// Navigation Events
export const NAVIGATION_EVENTS = {
  PAGE_VIEWED: "Page View", // Generic page view tracking
} as const;

// Grow Studio Events (Content Generator)
export const GROW_EVENTS = {
  HOOK_BATCH_GENERATED: "User generated a batch of hooks", // When user generates initial batch of hooks
  HOOK_GENERATION_COMPLETED: "Hook Generation Completed", // When hook generation process finishes
  ADDITIONAL_HOOKS_GENERATED: "Additional Hook Generation Completed", // When user generates more hooks after initial batch
  HOOK_VARIATIONS_GENERATED: "User generated variations of a hook", // When user creates variations of existing hooks
  HOOK_COPIED: "Hook Action Taken", // with action: "copy_to_clipboard"
  HOOK_LIKED: "Hook Action Taken", // with action: "like"/"unlike"
  HOOK_MARKED_FILMED: "Hook Action Taken", // with action: "mark_as_filmed"
  HOOK_MARKED_POSTED: "Hook Action Taken", // with action: "mark_as_posted"
  HOOK_REMOVED: "Hook Action Taken", // with action: "remove_variation"
  DIALOG_VIEW_CHANGED: "Dialog View Changed", // When user switches between different views in hook dialog
  MONETIZE_CLICKED: "Monetize Button Clicked", // When user clicks monetize button in grow studio
} as const;

// UTM Tracking (not really used atm, this is done by framer)
export const UTM_EVENTS = {
  UTM_CAMPAIGN_LANDING: "UTM Campaign Landing", // When user lands on page with UTM parameters
} as const;

// A/B Test Events
export const EXPERIMENT_EVENTS = {
  ONBOARDING_REDIRECT: "Onboarding Redirect Experiment", // A/B test for onboarding flow redirection
} as const;

// Marketing and Promotional Events
export const MARKETING_EVENTS = {
  TRY_FREE_AI_TOOLS_CLICKED: "Try free AI tools", // When user clicks "Try free AI tools" button
  CAMPAIGN_CLICKED: "Campaign Clicked", // When user clicks on a campaign
  BECOME_CIR: "Become a Creator-in-Residence", // When user clicks on "Become a Creator-in-Residence" button
} as const;

// Onboarding and Channel Events
export const ONBOARDING_CHANNEL_EVENTS = {
  CHANNEL_VIDEOS_RETRIEVED: "Onboarding Channel Videos Retrieved", // When user's channel videos are retrieved during onboarding
} as const;

// Auto-Enrollment Events
export const AUTO_ENROLLMENT_EVENTS = {
  CAMPAIGN_AUTO_ENROLLED_AFTER_LOGIN: "Campaign Auto-Enrolled After Login", // When user is automatically enrolled in campaign after login
} as const;

// Test Events
export const TEST_EVENTS = {
  TEST_EVENT: "Test Event", // For testing Mixpanel integration
} as const;

// Export all event constants
export const ANALYTICS_EVENTS = {
  ...AUTH_EVENTS,
  ...ONBOARDING_EVENTS,
  ...SOCIAL_EVENTS,
  ...STRIPE_EVENTS,
  ...APP_EVENTS,
  ...PARTNERSHIP_EVENTS,
  ...VIDEO_EVENTS,
  ...NAVIGATION_EVENTS,
  ...GROW_EVENTS,
  ...UTM_EVENTS,
  ...EXPERIMENT_EVENTS,
  ...MARKETING_EVENTS,
  ...ONBOARDING_CHANNEL_EVENTS,
  ...AUTO_ENROLLMENT_EVENTS,
  ...TEST_EVENTS,
} as const;

// Type for all event names
export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
